import { Purchases } from '@revenuecat/purchases-capacitor';
import { Capacitor } from '@capacitor/core';
import { isVirtualDeviceOrFalse } from '@/utils/nativeDevice';

class PurchaseService {
  constructor() {
    this.isInitialized = false;
    this.platform = Capacitor.getPlatform();
    // iOS-симулятор определяется асинхронно через Device.getInfo (раньше — по
    // слову Simulator в UA, которого там нет, см. utils/nativeDevice.js). До
    // ответа платформы считаем, что это устройство; каждый async-метод ждёт
    // ensureSimulatorChecked() перед проверкой this.isSimulator.
    this.isSimulator = false;
    this.simulatorCheck = null;
    this.currentUserId = null; // Track current user ID
  }

  // Симулятор — только iOS (RevenueCat на нём не работает); сбой плагина =
  // реальное устройство. isVirtual подменяется в юнитах.
  async checkIfSimulator(isVirtual = isVirtualDeviceOrFalse) {
    if (this.platform !== 'ios') return false;
    return isVirtual('RevenueCat');
  }

  // Один запрос к платформе на всё время жизни сервиса; результат — в this.isSimulator.
  ensureSimulatorChecked() {
    if (!this.simulatorCheck) {
      this.simulatorCheck = this.checkIfSimulator().then((value) => {
        this.isSimulator = value;
        return value;
      });
    }
    return this.simulatorCheck;
  }

  getApiKey() {
    // Determine API key based on platform
    if (this.platform === 'ios') {
      return 'appl_xeOiRuqzHoSraHeeUgFQYGgEFaL';
    } else if (this.platform === 'android') {
      return 'goog_GBhfCFcGyWeidKHJjLZBdqQHFTf';
    }
    // Fallback key for web/other platforms
    return '';
  }

  async initialize() {
    if (this.isInitialized) return;
    
    // Skip initialization on simulator
    await this.ensureSimulatorChecked();
    if (this.isSimulator) {
      console.warn('RevenueCat: iOS Simulator does not support purchases. Use a real device.');
      return;
    }
    
    try {
      const apiKey = this.getApiKey();
      await Purchases.configure({
        apiKey: apiKey,
      });
      this.isInitialized = true;
      console.log(`RevenueCat initialized for ${this.platform} with key: ${apiKey.substring(0, 10)}...`);
    } catch (error) {
      console.error('RevenueCat initialization error:', error);
      // Don't throw error for simulator/web to avoid breaking the app
      if (error.message && error.message.includes('not implemented')) {
        console.warn('RevenueCat: Platform does not support purchases. App will continue without this feature.');
        return;
      }
      throw error;
    }
  }

  async setUserId(userId) {
    // RevenueCat requires appUserID as a non-empty string
    if (!userId) {
      console.error('RevenueCat: userId is required');
      return;
    }
    
    // Convert to string if it's a number
    const userIdString = String(userId);
    
    // Check if user is already identified
    if (this.currentUserId === userIdString) {
      console.log(`RevenueCat: User ${userIdString} already identified, skipping`);
      return;
    }

    if (!this.isInitialized) {
      await this.initialize();
    }
    
    await this.ensureSimulatorChecked();
    if (this.isSimulator) {
      console.warn('RevenueCat: Skipping user identification on simulator');
      this.currentUserId = userIdString;
      return;
    }
    
    try {
      console.log(`RevenueCat: Identifying user as ${userIdString}...`);
      const { customerInfo } = await Purchases.logIn({ appUserID: userIdString });
      this.currentUserId = userIdString;
      console.log(`RevenueCat: User successfully identified as ${userIdString}`);
      return customerInfo;
    } catch (error) {
      console.error('RevenueCat: Error setting user ID:', error);
      throw error;
    }
  }

  /**
   * Set user attributes (email, displayName, phoneNumber, etc.)
   * 
   * @param {Object} attributes - User attributes
   * @param {String} attributes.email - User email (reserved attribute: $email)
   * @param {String} attributes.displayName - User display name (reserved attribute: $displayName)
   * @param {String} attributes.phoneNumber - User phone number (reserved attribute: $phoneNumber)
   * @param {Object} attributes.custom - Any custom attributes
   * 
   * Example:
   * await purchaseService.setAttributes({
   *   email: 'user@example.com',
   *   displayName: 'John Doe',
   *   phoneNumber: '+1234567890',
   *   custom: {
   *     company_id: '123',
   *     plan: 'premium'
   *   }
   * });
   */
  async setAttributes(attributes) {
    if (!this.isInitialized) {
      await this.initialize();
    }
    
    await this.ensureSimulatorChecked();
    if (this.isSimulator) {
      console.warn('RevenueCat: Skipping attributes on simulator');
      return;
    }
    
    try {
      const attributesToSet = {};
      
      // Map common attributes to reserved attributes (with $ prefix)
      if (attributes.email) {
        attributesToSet['$email'] = attributes.email;
      }
      if (attributes.displayName) {
        attributesToSet['$displayName'] = attributes.displayName;
      }
      if (attributes.phoneNumber) {
        attributesToSet['$phoneNumber'] = attributes.phoneNumber;
      }
      
      // Add any custom attributes
      if (attributes.custom) {
        Object.assign(attributesToSet, attributes.custom);
      }
      
      console.log('RevenueCat: Setting attributes:', Object.keys(attributesToSet));
      
      // Set all attributes at once
      await Purchases.setAttributes(attributesToSet);
      
      console.log('RevenueCat: Attributes set successfully');
    } catch (error) {
      console.error('RevenueCat: Error setting attributes:', error);
      throw error;
    }
  }

  async logout() {
    if (!this.isInitialized) return;
    
    await this.ensureSimulatorChecked();
    if (this.isSimulator) {
      console.log('RevenueCat: Skipping logout on simulator');
      this.currentUserId = null;
      return;
    }
    
    try {
      await Purchases.logOut();
      this.currentUserId = null;
      console.log('RevenueCat: User logged out');
    } catch (error) {
      console.error('RevenueCat: Error logging out:', error);
    }
  }

  async getProducts() {
    // Return empty array on simulator
    await this.ensureSimulatorChecked();
    if (this.isSimulator) {
      console.warn('RevenueCat: Products are not available on simulator');
      return [];
    }

    try {
      const offerings = await Purchases.getOfferings();
      return offerings.current?.availablePackages || [];
    } catch (error) {
      console.error('Error fetching products:', error);
      // If "not implemented" error, return empty array
      if (error.message && error.message.includes('not implemented')) {
        console.warn('RevenueCat: Platform does not support purchases');
        return [];
      }
      throw error;
    }
  }

  /**
   * Гарантирует, что SDK идентифицирован по main_company_id ПЕРЕД покупкой.
   * Без этого RevenueCat уходит анонимом ($RCAnonymousID) и платёж теряется на
   * бэкенде. setUserId самоидемпотентен (пропускает, если уже идентифицирован).
   */
  async ensureIdentified(mainCompanyId, attributes = null) {
    if (!mainCompanyId) {
      throw new Error('RevenueCat: cannot start purchase without main_company_id (user not identified)');
    }
    await this.setUserId(String(mainCompanyId));
    // Атрибуты ($email / main_company_id) выставляем перед покупкой из актуального
    // стора, чтобы вебхук нёс их сразу (не полагаясь только на watcher). Сбой
    // атрибутов НЕ блокирует покупку — для зачисления на верную компанию достаточно
    // appUserID (= main_company_id), уже гарантированного выше.
    if (attributes) {
      try {
        await this.setAttributes(attributes);
      } catch (error) {
        console.warn('RevenueCat: setAttributes before purchase failed (proceeding):', error);
      }
    }
    return this.currentUserId;
  }

  async purchaseProduct(mainCompanyId, attributes = null) {
    // Block purchases on simulator
    await this.ensureSimulatorChecked();
    if (this.isSimulator) {
      throw new Error('Purchases are not available on simulator. Use a real device.');
    }

    // Никогда не покупаем анонимно — сначала идентификация (+ атрибуты).
    await this.ensureIdentified(mainCompanyId, attributes);

    try {
      const offerings = await Purchases.getOfferings();
      const availablePackages = offerings.current?.availablePackages;
      
      if (!availablePackages || availablePackages.length === 0) {
        throw new Error('Products not found');
      }

      const packageToPurchase = availablePackages[0];
      const purchaseResult = await Purchases.purchasePackage({
        aPackage: packageToPurchase
      });

      return {
        product: packageToPurchase.product,
        customerInfo: purchaseResult.customerInfo
      };
    } catch (error) {
      // Проверяем отмену покупки разными способами
      const isCancelled = 
        error.code === 'PURCHASE_CANCELLED' || 
        error.code === 1 || // iOS user cancelled code
        error.userCancelled === true ||
        (error.message && error.message.toLowerCase().includes('cancel'));
      
      if (isCancelled) {
        console.log('Purchase cancelled by user');
        return null;
      }
      console.error('Purchase error:', error);
      throw error;
    }
  }

  getProductPrice(product) {
    return {
      price: product.price,
      currency: product.currencyCode,
      priceString: product.priceString
    };
  }

  isIOS() {
    return this.platform === 'ios';
  }

  isAndroid() {
    return this.platform === 'android';
  }
}

export default new PurchaseService();

